// HqPurchaseRequests — what the buyer has ordered, before any invoice exists.
//
// 2026-10-07 — the purchasing officer orders from the supplier, writes it in
// his manual book, and records it here the same day. Until now that figure
// lived only in the book, so "what is on order" could not be answered from the
// system at all.
//
// Deliberately thin. No line items, no approval, no accounting: the goods are
// already ordered by the time this is typed, and the detail belongs to the HQ
// Purchase raised when the invoice arrives.
import React, { useEffect, useState } from 'react';
import { FiPlus, FiRefreshCw, FiEdit2, FiXCircle, FiFileText, FiSearch } from 'react-icons/fi';
import {
  getHqPurchaseRequests, createHqPurchaseRequest, updateHqPurchaseRequest,
  cancelHqPurchaseRequest, getHqSuppliers, getHqBranches,
} from '../services/api';
import { sortBranches } from '../utils/sortBranches';
import Portal from '../utils/Portal';
import useModalScrollLock from '../utils/useModalScrollLock';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n) => `K${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate  = (s) => (s ? new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const STATUS = {
  OPEN:      { label: 'Open',      fg: '#b45309', bg: '#fef6e7', bd: '#fde68a' },
  PURCHASED: { label: 'Purchased', fg: '#15803d', bg: '#f0fdf4', bd: '#86efac' },
  CANCELLED: { label: 'Cancelled', fg: '#64748b', bg: '#f1f5f9', bd: '#e2e8f0' },
};
const TABS = ['OPEN', 'PURCHASED', 'CANCELLED', 'ALL'];

export default function HqPurchaseRequests() {
  const [rows, setRows]         = useState([]);
  const [totals, setTotals]     = useState({ open: 0, open_value: 0 });
  const [tab, setTab]           = useState('OPEN');
  const [q, setQ]               = useState('');
  const [query, setQuery]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [message, setMessage]   = useState(null);
  const [form, setForm]         = useState(null);   // null | {} = new | row = edit
  const [cancelling, setCancelling] = useState(null);
  const [suppliers, setSuppliers]   = useState([]);
  const [branches, setBranches]     = useState([]);

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 4000); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await getHqPurchaseRequests({ status: tab, q: query || undefined });
      setRows(r.data?.requests || []);
      setTotals(r.data?.totals || { open: 0, open_value: 0 });
    } catch (e) {
      flash('err', e?.response?.data?.error || 'Failed to load.');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, query]);
  useEffect(() => {
    getHqSuppliers().then(r => setSuppliers(r.data?.suppliers || r.data || [])).catch(() => {});
    getHqBranches().then(r => setBranches(sortBranches(r.data?.branches || []))).catch(() => {});
  }, []);

  const save = async () => {
    const f = form || {};
    if (!f.supplier_name)                      { flash('err', 'Choose a supplier.'); return; }
    if (!(parseFloat(f.value_requested) > 0))  { flash('err', 'Enter the value of the goods requested.'); return; }
    try {
      if (f.id) await updateHqPurchaseRequest(f.id, f);
      else      await createHqPurchaseRequest(f);
      setForm(null);
      flash('ok', f.id ? 'Request updated.' : 'Request recorded.');
      load();
    } catch (e) {
      flash('err', e?.response?.data?.error || 'Failed to save.');
    }
  };

  const doCancel = async () => {
    try {
      await cancelHqPurchaseRequest(cancelling.id, cancelling.reason || null);
      setCancelling(null);
      flash('ok', 'Request cancelled.');
      load();
    } catch (e) {
      flash('err', e?.response?.data?.error || 'Failed to cancel.');
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>Purchase Requests</h1>
          <p>What has been ordered from suppliers, before the invoice arrives</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') setQuery(q.trim()); }}
              placeholder="PR #, supplier, order # or book ref"
              style={{ ...inp, minWidth: 230 }} />
            <button onClick={() => setQuery(q.trim())} style={btnGhost}><FiSearch size={13} /> Search</button>
          </div>
          <button onClick={load} style={btnGhost}><FiRefreshCw size={13} /> Refresh</button>
          <button onClick={() => setForm({ date: todayISO() })} style={btnPrimary}><FiPlus size={14} /> New Request</button>
        </div>
      </div>

      {message && (
        <div style={{ padding: '10px 14px', marginBottom: 14, borderRadius: 8, fontSize: 13,
                      background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
                      color:      message.type === 'ok' ? '#166534' : '#b91c1c',
                      border: `1px solid ${message.type === 'ok' ? '#86efac' : '#fecaca'}` }}>
          {message.text}
        </div>
      )}

      {/* The figure that did not exist before this page: committed, not yet
          invoiced. Uses the app's own .stat-card so it matches every other
          page rather than being a hand-rolled box in the same screen. */}
      <div className="stat-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
        <div className="stat-card orange">
          <div className="stat-icon"><FiFileText /></div>
          <div>
            <div className="stat-label">On order</div>
            <div className="stat-value">{fmtMoney(totals.open_value)}</div>
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
              {totals.open} open request{totals.open === 1 ? '' : 's'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {TABS.map(s => {
          const on = tab === s;
          const label = s === 'ALL' ? 'All' : STATUS[s].label;
          return (
            <button key={s} onClick={() => setTab(s)}
              style={{ padding: '6px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                       border: `1px solid ${on ? '#13306b' : '#e2e6ee'}`,
                       background: on ? '#13306b' : '#fff', color: on ? '#fff' : '#334155' }}>
              {label}
            </button>
          );
        })}
      </div>

      <div className="data-table-container" style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ minWidth: 820 }}>
          <thead>
            <tr>
              <th>PR #</th><th>Date</th><th>Supplier</th>
              <th>Order #</th><th>Book Ref</th><th>Depot</th>
              <th style={{ textAlign: 'right' }}>Value</th>
              <th>Status</th><th>Became</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 36, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 36, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                <FiFileText /> {query ? `Nothing matches “${query}”.` : 'No requests here yet.'}
              </td></tr>
            ) : rows.map((r, i) => {
              const s = STATUS[r.status] || STATUS.OPEN;
              return (
                <tr key={r.id} style={{ background: i % 2 ? '#fcfcfd' : '#fff' }}>
                  <td style={{ fontWeight: 700 }}>{r.pr_number}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                  <td style={{ fontWeight: 600 }}>{r.supplier_name}</td>
                  <td style={{ color: '#475569' }}>{r.supplier_order_number || '—'}</td>
                  <td style={{ color: '#475569' }}>{r.reference || '—'}</td>
                  <td>{r.destination_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(r.value_requested)}</td>
                  <td>
                    <span style={{ padding: '2px 9px', borderRadius: 11, fontSize: 10.5, fontWeight: 700,
                                   background: s.bg, color: s.fg, border: `1px solid ${s.bd}`, whiteSpace: 'nowrap' }}>
                      {s.label}
                    </span>
                  </td>
                  <td style={{ color: '#0ea5e9', fontWeight: 600 }}>{r.purchase_number || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {/* Only an open request can be changed. Once a purchase is
                        against it, the request is the figure the variance is
                        measured from and editing would rewrite the question. */}
                    {r.status === 'OPEN' && (
                      <>
                        <button onClick={() => setForm({ ...r })} style={btnTiny} title="Edit this request">
                          <FiEdit2 size={11} /> Edit
                        </button>
                        <button onClick={() => setCancelling({ id: r.id, pr_number: r.pr_number, reason: '' })}
                          style={{ ...btnTiny, marginLeft: 6, color: '#b91c1c', borderColor: '#fecaca' }} title="Cancel this request">
                          <FiXCircle size={11} /> Cancel
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {form && (
        <RequestModal form={form} setForm={setForm} save={save} suppliers={suppliers} branches={branches} />
      )}

      {cancelling && (
        <Portal>
          <div style={overlay} onClick={() => setCancelling(null)}>
            <div style={{ ...modal, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Cancel {cancelling.pr_number}?</h3>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b' }}>
                Nothing is unwound — a request never touched stock or the ledger. It stays on file, marked cancelled.
              </p>
              <input value={cancelling.reason} onChange={e => setCancelling(c => ({ ...c, reason: e.target.value }))}
                placeholder="Reason (optional)" style={{ ...inp, width: '100%' }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => setCancelling(null)} style={btnGhost}>Keep it</button>
                <button onClick={doCancel} style={{ ...btnPrimary, background: '#dc2626', borderColor: '#dc2626' }}>
                  Cancel request
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}

function RequestModal({ form, setForm, save, suppliers, branches }) {
  useModalScrollLock(true);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Portal>
      <div style={overlay} onClick={() => setForm(null)}>
        <div style={modal} onClick={e => e.stopPropagation()}>
          <h3 style={{ margin: '0 0 2px', fontSize: 18 }}>{form.id ? `Edit ${form.pr_number}` : 'New Purchase Request'}</h3>
          <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#64748b' }}>
            {form.id ? 'Only open requests can be changed.' : 'The number is generated when you save.'}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 13 }}>
            <Field label="Date">
              <input type="date" value={String(form.date || '').slice(0, 10)}
                onChange={e => set('date', e.target.value)} style={inp} />
            </Field>

            <Field label="Supplier *">
              <select value={form.supplier_name || ''}
                onChange={e => {
                  const s = suppliers.find(x => x.name === e.target.value);
                  setForm(f => ({ ...f, supplier_name: e.target.value,
                                  supplier_id: s?.id || null, supplier_sync_id: s?.sync_id || null }));
                }} style={inp}>
                <option value="">Choose…</option>
                {suppliers.map(s => <option key={s.id || s.sync_id} value={s.name}>{s.name}</option>)}
              </select>
            </Field>

            <Field label="Value requested *" hint="Estimate. The invoice will differ, and that is expected.">
              <input type="number" step="0.01" min="0" value={form.value_requested ?? ''}
                onChange={e => set('value_requested', e.target.value)}
                placeholder="0.00" style={{ ...inp, fontFamily: 'monospace', fontWeight: 600 }} />
            </Field>

            <Field label="Supplier's order #" hint="Their reference, if he was given one.">
              <input value={form.supplier_order_number || ''}
                onChange={e => set('supplier_order_number', e.target.value)}
                placeholder="e.g. SO-884127" style={inp} />
            </Field>

            {/* Added 2026-10-07 at the user's request: the page number in the
                buyer's manual book, so a row here can be found in the ledger
                and the other way round. */}
            <Field label="Book reference" hint="The number from the manual book.">
              <input value={form.reference || ''}
                onChange={e => set('reference', e.target.value)}
                placeholder="e.g. Bk 4 / p. 112" style={inp} />
            </Field>

            <Field label="Destination depot" hint="Optional — leave blank if not yet decided.">
              <select value={form.destination_slug || ''} onChange={e => set('destination_slug', e.target.value)} style={inp}>
                <option value="">Not decided</option>
                {branches.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ marginTop: 13 }}>
            <Field label="Notes">
              <input value={form.notes || ''} onChange={e => set('notes', e.target.value)}
                placeholder="Anything worth remembering about this order" style={{ ...inp, width: '100%' }} />
            </Field>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
            <button onClick={() => setForm(null)} style={btnGhost}>Cancel</button>
            <button onClick={save} style={btnPrimary}>{form.id ? 'Save changes' : 'Save Request'}</button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5,
                    textTransform: 'uppercase', color: '#64748b', marginBottom: 5 }}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>{hint}</div>}
  </div>
);

const inp = { padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13,
              background: '#fff', color: '#0f172a', boxSizing: 'border-box', width: '100%' };
const btnPrimary = { padding: '8px 16px', background: '#13306b', color: '#fff', border: '1px solid #13306b',
                     borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                     display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnGhost = { padding: '8px 14px', background: '#fff', color: '#334155', border: '1px solid #e2e8f0',
                   borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                   display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnTiny = { padding: '4px 9px', background: '#fff', color: '#334155', border: '1px solid #e2e8f0',
                  borderRadius: 6, cursor: 'pointer', fontSize: 11.5, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 4 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modal = { background: '#fff', borderRadius: 12, padding: 22, width: '100%', maxWidth: 680,
                maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
